<script lang="ts">
export default {
  name: "MainFobizzNavbar",
};
</script>

<script setup lang="ts">
import mySchoolIcon from "/images/Meine_Schule_Icon.svg";
import logoUrlMobile from "/images/fobizz-logo-F.png";
import logoUrlDesktop from "/images/fobizz_logo_4c.svg";

const userStore = useUserStore();

const { t } = useI18n();

const searchIsActive = ref(false);
</script>

<template>
  <a href="#main-content" class="skip-to-content-link">
    {{ t("mainFobizzNavbar.a11y.skipToContent") }}
  </a>

  <div class="startpage-navbar-wrapper">
    <div class="navbar main-fobizz-navbar">
      <div class="container">
        <div class="d-flex">
          <a
            href="/"
            class="navbar-brand d-none d-lg-flex align-items-center"
            aria-label="Go to homepage"
          >
            <img :src="logoUrlDesktop" width="110" alt="" />
          </a>
          <a
            href="/"
            class="d-flex align-items-center d-lg-none"
            aria-label="Go to homepage"
          >
            <img :src="logoUrlMobile" width="40" alt="" />
          </a>
          <div v-if="!searchIsActive" class="me-2">
            <MainFobizzNavbarTabs />
          </div>
        </div>

        <template v-if="userStore.isAuthenticated">
          <MainFobizzNavbarSearch
            @activated="searchIsActive = true"
            @deactivated="searchIsActive = false"
          />

          <nav aria-label="Actions">
            <ul class="navbar-nav">
              <li class="nav-item d-none d-lg-flex">
                <router-link
                  v-tooltip.bottom="
                    t('mainFobizzNavbar.myfobizz.links.contact')
                  "
                  :aria-label="t('mainFobizzNavbar.myfobizz.links.contact')"
                  class="main-fobizz-navbar__link main-fobizz-navbar__nav-icon"
                  :to="{ name: 'help-redirect' }"
                  data-click-tracking="true"
                >
                  <BaseIcon
                    icon-name="question-circle"
                    color="inherit"
                    style="font-size: 1.2rem"
                  />
                </router-link>
              </li>
              <li
                v-if="!userStore.user?.international"
                class="nav-item d-none d-lg-flex"
              >
                <a
                  v-tooltip.bottom="t('mainFobizzNavbar.tooltips.news')"
                  :aria-label="t('mainFobizzNavbar.tooltips.news')"
                  class="main-fobizz-navbar__link main-fobizz-navbar__nav-icon"
                  href="/posts"
                  data-click-tracking="true"
                >
                  <MainFobizzNavbarNotificationIcon style="font-size: 1.2rem" />
                </a>
              </li>
              <li class="nav-item d-none d-lg-flex">
                <a
                  v-tooltip.bottom="t('mainFobizzNavbar.tooltips.mySchool')"
                  class="main-fobizz-navbar__link main-fobizz-navbar__nav-icon"
                  href="/schule"
                  :aria-label="t('mainFobizzNavbar.tooltips.mySchool')"
                >
                  <BaseIcon :size="22" :icon-svg="mySchoolIcon" />
                </a>
              </li>
              <li class="nav-item">
                <MainFobizzNavbarMyFobizzOffcanvas />
              </li>
            </ul>
          </nav>
        </template>
        <template v-else>
          <ul class="navbar-nav">
            <MainFobizzNavbarLoginDropdown class="d-none d-lg-flex" />
            <MainFobizzNavbarLoginOffcanvas
              signup-url="/users/sign_up"
              class="d-flex d-lg-none"
            />
            <li class="nav-item ms-2 d-none d-lg-flex">
              <a
                href="/users/sign_up"
                class="btn btn-primary btn-rounded text-nowrap"
                target="_blank"
              >
                {{ t("links.signup") }}
              </a>
            </li>
          </ul>
        </template>
      </div>
    </div>
    <slot name="tabs"></slot>
  </div>
</template>

<style scoped lang="scss">
.startpage-navbar-wrapper {
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  z-index: 1030;
  max-width: 100vw;
}

.skip-to-content-link {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  background: $color-white;
  padding: $spacer-2 $spacer-3;
  border: 1px solid $border-color;
  border-radius: $border-radius;
  color: $body-color;
  text-decoration: none;
  z-index: 1031;
  top: -100px;

  &:focus-visible {
    top: $spacer-2;
  }
}
</style>

<!-- // Global Styles shared in nested components, align with plattform -->
<style lang="scss">
.navbar.main-fobizz-navbar,
.main-fobizz-navbar {
  height: $navbar-height;
  display: flex;
  align-items: center;
  padding: 0;
  background-color: $color-white;
  border-bottom: 1px solid $border-color;

  .navbar-nav {
    flex-direction: row;
    align-items: center;
  }

  &.navbar > .container {
    flex-wrap: nowrap !important;
  }

  &__link {
    position: relative;
    color: $body-color;
    display: flex;
    align-items: center;
    height: $navbar-height;
    font-size: 1rem;
    padding: 0 $spacer-3;
    text-decoration: none;

    @include media-breakpoint-down(md) {
      padding: 0 $spacer-2;
    }

    &::before {
      content: "";
      position: absolute;
      bottom: 0;
      left: $spacer-3;
      right: $spacer-3;
      height: 2px;
      background-color: $brand-courses;
      display: none;
    }

    &.active::before,
    &:hover::before {
      display: flex;
    }

    &--sm {
      padding: 0 $spacer-2;
    }
  }

  &__nav-icon {
    border: none;
    border-radius: 0;
    padding: 0.375rem 0.75rem;
    background-color: transparent;

    &::before {
      left: 0.75rem;
      right: 0.75rem;
    }
  }

  & .nav-item:last-of-type &__nav-icon {
    // removing the padding will change the pseudo element's width
    margin-right: -0.5rem;
  }
}
</style>
